import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/lib/store";
import { translations, BRAND } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { lang } = useStore();
  const t = translations[lang];
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact_sent);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact_error);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="contact-page">
      <p
        className="text-xs tracking-[0.5em] text-yellow-400/80 mb-3"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        {t.contact_eyebrow}
      </p>
      <h1
        className="text-5xl sm:text-6xl font-black"
        style={{
          fontFamily: '"Bebas Neue", system-ui',
          background: "linear-gradient(180deg, #fff, #F4D77A)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {t.contact_title}
      </h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t.contact_subtitle}</p>

      <div className="grid md:grid-cols-5 gap-10 mt-12">
        <form
          onSubmit={submit}
          className="md:col-span-3 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              required
              placeholder={t.contact_name}
              value={form.name}
              onChange={onChange("name")}
              data-testid="contact-name-input"
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-yellow-500/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400"
            />
            <input
              required
              type="email"
              placeholder={t.contact_email}
              value={form.email}
              onChange={onChange("email")}
              data-testid="contact-email-input"
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-yellow-500/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400"
            />
          </div>
          <input
            placeholder={t.contact_phone}
            value={form.phone}
            onChange={onChange("phone")}
            data-testid="contact-phone-input"
            className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-yellow-500/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400"
          />
          <textarea
            required
            rows={6}
            placeholder={t.contact_message}
            value={form.message}
            onChange={onChange("message")}
            data-testid="contact-message-input"
            className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-yellow-500/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400 resize-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="px-7 py-3.5 rounded-full font-bold tracking-widest text-black inline-flex items-center gap-2 disabled:opacity-50 hover:scale-[1.02] transition-transform"
            style={{
              background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
              boxShadow: "0 12px 30px -8px rgba(212,175,55,0.5)",
            }}
          >
            <Send className="w-4 h-4" />
            {t.contact_send}
          </button>
        </form>

        <aside className="md:col-span-2 space-y-5">
          {[
            { Icon: MapPin, label: t.contact_address, value: t.contact_address_value, test: "contact-address" },
            { Icon: Phone, label: t.contact_phone_label, value: `${BRAND.phone} · ${BRAND.phone_alt} · ${BRAND.phone_alt2}`, test: "contact-phone-info" },
            { Icon: Mail, label: t.contact_email_label, value: BRAND.email, test: "contact-email-info" },
          ].map(({ Icon, label, value, test }) => (
            <div
              key={test}
              data-testid={test}
              className="p-5 rounded-xl border border-yellow-500/20 bg-white/[0.02] flex gap-4 items-start"
            >
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/80 mb-1">{label}</p>
                <p className="text-white text-sm">{value}</p>
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
